import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Alert, Row, Col, Badge, InputGroup } from 'react-bootstrap';
import api from '../lib/api';

const MODELS = ['mistral', 'tinyllama', 'openai', 'gemini', 'groq'];

const AdminModelSelector = () => {
  const [currentModel, setCurrentModel] = useState('');
  const [groqModel, setGroqModel] = useState('');

  // write-only inputs
  const [openaiKey, setOpenaiKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [groqKey, setGroqKey]     = useState('');

  // read-only flags + masked
  const [hasOpenAI, setHasOpenAI] = useState(false);
  const [hasGemini, setHasGemini] = useState(false);
  const [hasGroq, setHasGroq]     = useState(false);

  const [openaiMasked, setOpenaiMasked] = useState('');
  const [geminiMasked, setGeminiMasked] = useState('');
  const [groqMasked,   setGroqMasked]   = useState('');

  const [lastUpdated, setLastUpdated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setMessage('');
      const res = await api.get('/chat-model/');
      const data = res.data || {};

      setCurrentModel(data.current_model || '');
      setGroqModel(data.groq_model || '');

      setHasOpenAI(!!data.has_openai_key);
      setHasGemini(!!data.has_gemini_key);
      setHasGroq(!!data.has_groq_key);

      setOpenaiMasked(data.openai_key_masked || '');
      setGeminiMasked(data.gemini_key_masked || '');
      setGroqMasked(data.groq_key_masked || '');

      setLastUpdated(data.updated_at || null);

      setOpenaiKey('');
      setGeminiKey('');
      setGroqKey('');
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load model config';
      setMessage(`Error: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        model: (currentModel || '').trim(),
        groq_model: (groqModel || '').trim(),
      };
      if (openaiKey.trim()) payload.openai_key = openaiKey.trim();
      if (geminiKey.trim()) payload.gemini_key = geminiKey.trim();
      if (groqKey.trim())   payload.groq_key   = groqKey.trim();

      const res = await api.post('/chat-model/', payload);
      const data = res.data || {};

      // reflect server state
      setHasOpenAI(!!data.has_openai_key);
      setHasGemini(!!data.has_gemini_key);
      setHasGroq(!!data.has_groq_key);

      if (typeof data.openai_key_masked === 'string') setOpenaiMasked(data.openai_key_masked);
      if (typeof data.gemini_key_masked === 'string') setGeminiMasked(data.gemini_key_masked);
      if (typeof data.groq_key_masked === 'string')   setGroqMasked(data.groq_key_masked);

      if (typeof data.current_model === 'string') setCurrentModel(data.current_model);
      if (typeof data.groq_model === 'string') setGroqModel(data.groq_model);

      setLastUpdated(data.updated_at || new Date().toISOString());
      setMessage(data.message || 'Saved');

      setOpenaiKey('');
      setGeminiKey('');
      setGroqKey('');
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Save failed';
      setMessage(`Error: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const KeyRow = ({ label, masked, has, value, onChange, placeholder }) => (
    <Card className="mb-3 p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>{label}</strong>
        {has ? <Badge bg="success">Key set</Badge> : <Badge bg="secondary">Not set</Badge>}
      </div>
      <Row className="g-3">
        <Col md={7}>
          <Form.Group>
            <Form.Label className="mb-1">Current (masked)</Form.Label>
            <Form.Control type="text" value={masked || ''} readOnly />
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group>
            <Form.Label className="mb-1">New key (optional)</Form.Label>
            <Form.Control
              type="password"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
            />
          </Form.Group>
        </Col>
      </Row>
    </Card>
  );

  if (loading) return <Spinner animation="border" />;

  return (
    <Card className="shadow-sm mb-3 p-3">
      <h5 className="mb-3">⚙️ AI Model Configuration</h5>

      <Row className="g-3 mb-2">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Active Model</Form.Label>
            <Form.Select
              value={currentModel}
              onChange={(e) => setCurrentModel(e.target.value)}
              disabled={saving}
            >
              {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>

        {currentModel === 'groq' && (
          <Col md={6}>
            <Form.Group>
              <Form.Label>Groq Model ID</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={groqModel}
                  onChange={(e) => setGroqModel(e.target.value)}
                  placeholder="e.g. llama-3.1-8b-instruct"
                />
                <InputGroup.Text className="text-muted">model</InputGroup.Text>
              </InputGroup>
              <div className="text-muted small mt-1">
                Use any valid Groq model enabled for your API key.
              </div>
            </Form.Group>
          </Col>
        )}
      </Row>

      {currentModel === 'openai' && (
        <KeyRow
          label="OpenAI API Key"
          masked={openaiMasked}
          has={hasOpenAI}
          value={openaiKey}
          onChange={setOpenaiKey}
          placeholder={hasOpenAI ? 'Leave empty to keep current' : 'sk-...'}
        />
      )}

      {currentModel === 'gemini' && (
        <KeyRow
          label="Gemini API Key"
          masked={geminiMasked}
          has={hasGemini}
          value={geminiKey}
          onChange={setGeminiKey}
          placeholder={hasGemini ? 'Leave empty to keep current' : 'AIza...'}
        />
      )}

      {currentModel === 'groq' && (
        <KeyRow
          label="Groq API Key"
          masked={groqMasked}
          has={hasGroq}
          value={groqKey}
          onChange={setGroqKey}
          placeholder={hasGroq ? 'Leave empty to keep current' : 'gsk_...'}
        />
      )}

      <div className="d-flex align-items-center gap-2">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" animation="border" /> : 'Save Configuration'}
        </Button>
        {lastUpdated && <span className="text-muted small">Last updated: {new Date(lastUpdated).toLocaleString()}</span>}
      </div>

      {message && (
        <Alert
          variant={message.startsWith('Error') ? 'danger' : 'info'}
          className="mt-3"
        >
          {message}
        </Alert>
      )}
    </Card>
  );
};

export default AdminModelSelector;
